import { useEffect, useRef, useState } from 'react';
import { useUserPreferencesStore } from '../stores/userPreferencesStore';
import { usePharmacyStore } from '../stores/pharmacyStore';
import { useMyRoleLandingPage } from './queries/useRoleLandingPages';
import type { MyRoleLandingPage } from '../services/roleLandingPagesService';
import type { WorkflowDefaults } from '../services/userPreferencesService';

/**
 * Fallback landing page used when the user has no saved preference, or one
 * could not be loaded in time. Single source of truth for this path — don't
 * hard-code it anywhere else (see `.claude/rules` "one source of truth").
 */
export const DEFAULT_LANDING_PAGE = '/order-fulfillment/operations-dashboard';

/** PRX-124: shown when the role's landing page is set but the user may not open it. */
export const ROLE_LANDING_UNAVAILABLE_NOTICE =
  "Your role's landing page isn't available to you. Showing the dashboard.";

/**
 * PRX-124 — the user's own landing page, or null when they have not chosen
 * one. Profile -> Workflow saves a pick with `landingPageChosen: true`, and
 * that wins whatever the page (the dashboard included). A stored dashboard
 * WITHOUT the flag is the default the backend used to seed for every user,
 * so it is not a choice. A stored non-dashboard page without the flag was
 * picked before the flag existed, so it still is.
 */
export function ownLandingPage(workflowDefaults: WorkflowDefaults | null | undefined): string | null {
  const page = workflowDefaults?.defaultLandingPage;
  if (!page) return null;
  if (workflowDefaults?.landingPageChosen === true) return page;
  if (workflowDefaults?.landingPageChosen === false) return null;
  return page === DEFAULT_LANDING_PAGE ? null : page;
}

/**
 * PRX-124 — what Profile -> Workflow saves. The landing page is written only
 * when the user touched the select, so saving the other workflow settings
 * never turns the default into a choice. `''` is "Use my role's page" and
 * clears the choice.
 */
export function workflowDefaultsToSave(
  local: { defaultLandingPage: string; preferredQueues: string[] },
  landingPageTouched: boolean,
): Partial<WorkflowDefaults> {
  const toSave: Partial<WorkflowDefaults> = { preferredQueues: local.preferredQueues };
  if (landingPageTouched) {
    toSave.defaultLandingPage = local.defaultLandingPage;
    toSave.landingPageChosen = local.defaultLandingPage !== '';
  }
  return toSave;
}

/**
 * PRX-124 — the sign-in order, pure:
 *  1. the user's own page ({@link ownLandingPage} — pass its result);
 *  2. the role's page at the active pharmacy, when the role may open it;
 *  3. the operations dashboard. A role page that is set but not open to the
 *     user lands here WITH a notice, never silently.
 */
export function resolveLandingPage(input: {
  userPage: string | null | undefined;
  roleLanding: MyRoleLandingPage | null | undefined;
}): { path: string; notice: string | null } {
  const { userPage, roleLanding } = input;
  if (userPage) {
    return { path: userPage, notice: null };
  }
  if (roleLanding?.landingPage) {
    return roleLanding.allowed
      ? { path: roleLanding.landingPage, notice: null }
      : { path: DEFAULT_LANDING_PAGE, notice: ROLE_LANDING_UNAVAILABLE_NOTICE };
  }
  return { path: DEFAULT_LANDING_PAGE, notice: null };
}

// Bound on how long the "/" redirect waits for preferences to load before
// falling back to the default (PRX-40 R2) — never sit on a blank/loading
// page indefinitely.
const READY_TIMEOUT_MS = 5000;

/**
 * Resolves the page a just-authenticated user should land on, reactively.
 *
 * PRX-40: `App.tsx` used to compute this once from a synchronous
 * `localStorage.getItem('user-preferences-store')` read and never revisited
 * it, so a browser with nothing cached yet (fresh profile, cleared site
 * data, incognito, first login on a device) always fell back to
 * {@link DEFAULT_LANDING_PAGE} even though the user's real preference was
 * about to arrive from the server.
 *
 * `userPreferencesStore.preferences` is otherwise only fetched by
 * `MainLayout` (mounted on the destination route, i.e. only *after* this
 * redirect has already fired) and `Profile` — neither renders while the app
 * is still sitting on "/". So this hook also kicks off the fetch itself,
 * once the active pharmacy id is known, so the common "saved preference,
 * uncached browser" case actually resolves instead of always riding out the
 * timeout below.
 *
 * `ready` becomes `true` as soon as any of the following hold:
 *  - `preferences` has loaded (from the persisted cache or a fresh fetch)
 *  - the fetch has settled with an error
 *  - {@link READY_TIMEOUT_MS} has elapsed without either of the above
 *    (falls back to the default path and logs a warning once)
 */
export function useDefaultLandingPage(): { path: string; ready: boolean; notice: string | null } {
  const preferences = useUserPreferencesStore((state) => state.preferences);
  const loading = useUserPreferencesStore((state) => state.loading);
  const error = useUserPreferencesStore((state) => state.error);
  const fetchPreferences = useUserPreferencesStore((state) => state.fetchPreferences);
  const activePharmacyId = usePharmacyStore((state) => state.activePharmacy?.id);

  const loaded = preferences !== null || !!error;
  const [timedOut, setTimedOut] = useState(false);
  const requestedRef = useRef(false);

  // Trigger the fetch ourselves instead of waiting on MainLayout to mount —
  // it never will while we're holding the "/" redirect.
  useEffect(() => {
    if (!loaded && !loading && activePharmacyId && !requestedRef.current) {
      requestedRef.current = true;
      fetchPreferences(activePharmacyId);
    }
  }, [loaded, loading, activePharmacyId, fetchPreferences]);

  const userPage = ownLandingPage(preferences?.workflowDefaults);
  const hasOwnPage = userPage !== null;

  // PRX-124: the role's page is only needed when the user has no page of
  // their own. It is asked of the active pharmacy; with none active there is
  // no role to ask about, so that counts as settled rather than riding out
  // the timeout.
  const roleQuery = useMyRoleLandingPage(activePharmacyId, loaded && !hasOwnPage);
  const roleSettled = !activePharmacyId || roleQuery.isSuccess || roleQuery.isError;

  useEffect(() => {
    if (roleQuery.isError) {
      console.warn(
        "useDefaultLandingPage: could not load the role's landing page; using the default landing page.",
        roleQuery.error,
      );
    }
  }, [roleQuery.isError, roleQuery.error]);

  const ready = (loaded && (hasOwnPage || roleSettled)) || timedOut;

  useEffect(() => {
    if (ready) {
      return;
    }
    const timer = setTimeout(() => {
      console.warn(
        'useDefaultLandingPage: preferences did not load within 5s; using the default landing page.',
      );
      setTimedOut(true);
    }, READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [ready]);

  const { path, notice } = resolveLandingPage({
    userPage,
    roleLanding: activePharmacyId ? roleQuery.data : null,
  });

  return { path, ready, notice };
}
